import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'

export const Route = createFileRoute('/_site/compare/newrelic')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'New Relic Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs New Relic: New Relic bills per user, data, and synthetic check. Uptrack has flat plans from $3/mo with 30-second checks." },
      { property: 'og:title', content: 'New Relic Alternative — Uptrack' },
      { property: 'og:description', content: "New Relic includes 500 free synthetic checks a month, then $0.005 per check. Uptrack Pro is $3/mo for 50 monitors at 30s." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-newrelic.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/newrelic' },
    ],
    scripts: comparisonScripts('newrelic', "New Relic"),
  }),
})

const data: ComparisonData = {
  competitor: "New Relic",
  tagline: "New Relic is a full observability platform with a free tier of 100 GB ingest and 500 synthetic checks a month. For uptime checks beyond that, Uptrack offers flat plans from $3/mo.",
  heroDescription: "These facts come from New Relic's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Free tier", uptrack: "5 monitors at 30 s from Nuremberg", competitor: "100 GB ingest, 1 full user, 500 synthetic checks/mo" },
    { feature: "Synthetic check pricing", uptrack: "$3/mo for 50 monitors at 30s", competitor: "$0.005 per check beyond the free 500", uptrackWins: true },
    { feature: "Fastest check interval", uptrack: "30 sec", competitor: "1 min", uptrackWins: true },
    { feature: "User pricing", uptrack: "Seats included (3, 5, or 15 by plan)", competitor: "Per user: core $49/mo, full platform from $99/mo", uptrackWins: true },
    { feature: "Alert confirmation", uptrack: "2-5 consecutive failed checks", competitor: "2 retries at the same location; multi-location conditions" },
    { feature: "APM", uptrack: "Not available", competitor: "Full suite included" },
    { feature: "Browser monitoring", uptrack: "Playwright checks", competitor: "RUM + scripted browsers" },
  ],

  painPoints: [
    {
      title: "Per-check pricing",
      description: "Beyond 500 free checks a month, New Relic bills $0.005 per synthetic check. One monitor checked every minute from one location runs about 43,200 checks per 30 days, or about $216. Uptrack Pro checks 50 monitors every 30 seconds for $3/mo.",
    },
    {
      title: "Per-user pricing",
      description: "New Relic bills users: $49/mo per core user, and full platform users on Standard cost $10 for the first and $99 for each additional user. Uptrack plans include seats: 3 on Pro, 5 on Team, 15 on Business.",
    },
    {
      title: "1-minute minimum interval",
      description: "New Relic synthetic monitors run at most once per minute. Uptrack checks every 30 seconds on every plan.",
    },
  ],

  differentiators: [
    "Flat-rate pricing with no per-seat or per-check charges.",
    "30-second checks on every plan, from $3/mo for 50 monitors.",
    "Status pages included on every plan.",
    "Simple setup in minutes. No agents, no NRQL queries, no quota math.",
    "Alert confirmation with 2-5 consecutive failed checks.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you need full APM with distributed tracing, error tracking, and code-level performance analysis.",
    "If you need Real User Monitoring (RUM) for frontend performance from real browser sessions.",
    "If you need to correlate uptime with application metrics, logs, and traces in one platform.",
    "If your team already uses New Relic and you want everything in one observability suite.",
    "If your uptime needs fit in 500 checks a month, New Relic's free tier may cover them.",
  ],
}
